/*
*   @site           Preloaded: Site
*   @function       Keyline Controller
*   @author         Last17
*   @copyright      Copyright 2025, Preloaded.com
*   @version        0.01
*
*********************************************************************************************/


//-----------------------------------------------
// Imports
//-----------------------------------------------

//Modules
import LayoutController from '../_controllers/LayoutController.js';
import ModuleController from '../_controllers/ModuleController.js';

//Libs
import { gsap } from 'gsap';

//Globals
import * as Globals from '../_constants/Globals';



//-----------------------------------------------
// Class Definition
//-----------------------------------------------
class _KeylineController {

  //-----------------------------------------------
  // Constructor
  //-----------------------------------------------
  constructor() {
    //List
    this.modules = [];
    this.keylines = [];

    //Timelines
    this.keylinesGeneric = [];

    //Items with no keylines
    this.skipForKeylines = ['module--hero', 'module--carousel-specialism', 'module--lens-wipe'];

    //Keyline!
    this.keyline = `<svg xmlns="http://www.w3.org/2000/svg" version="1.1" viewBox="0 0 273.4 273.4">
        <g>
            <g id="Layer_2">
            <g id="Layer_1-2">
                <path style="stroke-miterlimit: 1;fill:none;" d="M24.5,24.5C58.8,9.1,96.7.5,136.7.5s77.9,8.6,112.1,24c15.4,34.2,24.1,72.2,24.1,112.1s-8.6,77.9-24.1,112.1c-34.2,15.4-72.2,24-112.1,24s-77.9-8.6-112.1-24.1C9.1,214.6.5,176.7.5,136.7S9.1,58.8,24.5,24.5h0Z"/>
            </g>
            </g>
        </g>
    </svg>`;

    //Listen for resize
    PubSub.subscribe('resize', (id, data) => { this.resizeEvent(data); });
  }




  //-----------------------------------------------
  // Init
  //-----------------------------------------------
  init() {
    //Store the list
    this.modules = ModuleController.getModules();


    //Inject Top Keyline
    (this.modules.length > 0) ? this.keylineInject(0, 'top') : null;

    //Inject Bottom Keyline (skip footer module)
    (this.modules.length > 3) ? this.keylineInject((this.modules[this.modules.length - 1].element.classList.contains('module--footer')) ? this.modules.length - 2 : this.modules.length - 1, 'bottom') : null;
    
    //Index all keylines
    this.keylines = document.querySelectorAll('.keyline');

    //Listen to scrol events
    LayoutController.listenToScroll({
      scope:this,
      func: this.scrollEvent
    });

    //Resize
    this.resizeEvent(null);
  }


  //-----------------------------------------------
  // Resize Event
  //-----------------------------------------------
  resizeEvent(data) {
    //Keyline stroke width across the whole site
    for(let i = 0; i < this.keylines.length; i++) {
      if(this.keylines[i].querySelector('path')) {
        this.keylines[i].querySelector('path').style['stroke-width'] =  Globals.STROKE_CONSTANT / this.keylines[i].querySelector('svg').getBoundingClientRect().width;
      }
    }

    //Re-calculate variables for our keylines
    let sizeFactor;
    let size;
    for(let i = 0; i < this.keylinesGeneric.length; i++) {
      //Overall height (NavigationController uses this for the alt nav
      this.keylinesGeneric[i].height = this.getGroupHeight(this.keylinesGeneric[i].elements);

      //Bottom aligned keylines have a dynamic size
      if(this.keylinesGeneric[i].type === 'bottom') {
        sizeFactor = (window.innerWidth < 768) ? 3 : 1.7;
        size = Math.min(this.keylinesGeneric[i].element.offsetWidth, this.keylinesGeneric[i].element.offsetHeight);
        this.keylinesGeneric[i].element.querySelector('.keyline svg').style.width = size * sizeFactor;
        this.keylinesGeneric[i].element.querySelector('.keyline svg').style.height = size * sizeFactor;
      }
    }

    //Scroll check
    this.scrollEvent(null);
  }


  //-----------------------------------------------
  // Scroll event
  //-----------------------------------------------
  scrollEvent(data) {
    //Adjust any timelines
    let scrollBounds;
    for(let i = 0; i < this.keylinesGeneric.length; i++) {
      //Bounds for this item
      scrollBounds = this.getScrollBounds(this.keylinesGeneric[i].elements);

      //Calculate a scroll proportion
      let prop = (window.scrollY - scrollBounds.min) / (scrollBounds.max - scrollBounds.min);
      prop = Math.min(1, Math.max(0, prop)); //clamped
      
      //Adjust the designated timeline accordingly
      this.keylinesGeneric[i].timeline.seek(prop);
    }
  }


  //-----------------------------------------------
  // Get scroll bounds
  //-----------------------------------------------
  getScrollBounds(items) {
    //Bounds
    let bounds = {
      min:(items[0].offsetTop > window.innerHeight) ? items[0].offsetTop - window.innerHeight : 0,
      max: items[0].offsetTop
    };

    //Add to max
    for(let i = 0; i < items.length; i++) {
      bounds.max += items[i].offsetHeight;
    }

    //Max can't be higher than the maximum scroll of the page
    bounds.max = Math.min(bounds.max, document.documentElement.scrollHeight - window.innerHeight);

    //Return
    return bounds;
  }


  //-----------------------------------------------
  // Get group height
  //-----------------------------------------------
  getGroupHeight(list) {
    let height = 0;
    for(let i  = 0; i < list.length; i++) {
      height += list[i].offsetHeight;
    }
    return height;
  }


  //-----------------------------------------------
  // Get themes for a given set of modules
  //-----------------------------------------------
  getModuleThemes(element) {

    //Vars
    let previousElement, nextElement;

    //Next
    if(element.parentNode.nextElementSibling && element.parentNode.nextElementSibling.nodeName === 'SECTION') {
      for (const child of element.parentNode.nextElementSibling.children) {
        if(!nextElement) {
          nextElement = child;
          break;
        }
      }
    }

    //Previous element
    if(element.parentNode.previousElementSibling && element.parentNode.previousElementSibling.nodeName === 'SECTION') {
      for (const child of element.parentNode.previousElementSibling.children) {
        if(!previousElement) {
          previousElement = child;
          break;
        }
      }
    }


    //Return
    return {
      current: this.getModuleTheme(element),
      previous: previousElement ? this.getModuleTheme(previousElement) : '',
      next: nextElement ? this.getModuleTheme(nextElement) : '',
      previousIsFlourish: previousElement ? previousElement.classList.contains('flourish') : false,
      nextIsFlourish: nextElement ? nextElement.classList.contains.hasClass('flourish') : false,
    };
  }


  //-----------------------------------------------
  // Get the theme for a given module
  //-----------------------------------------------
  getModuleTheme(element) {
    let classes = element.getAttribute('class').split(' ');
    for(let i = 0; i < classes.length; i++) {
      if(classes[i].indexOf('theme-') === 0) {
        return classes[i].split('-')[1];
      }
    }
    return '';
  }


  //-----------------------------------------------
  // Inject keyline
  //-----------------------------------------------
  keylineInject(id, type) {
    //Check for restricted items
    for(let i = 0; i < this.skipForKeylines.length; i++) {
      if(this.modules[id].element.classList.contains(this.skipForKeylines[i])) {
        return;
      }
    }

    //Target element
    let targetElement = this.modules[id].element;
    let isFlourish = false;
    let flipped = false;
    let elements = [this.modules[id].element];
    let valid = true;
    let classAlign = '';
    let rotation = {start:14, end: 2};

    //Look for a keyline override colour
    let keylineTheme = (targetElement.getAttribute('data-keyline-theme')) ? targetElement.getAttribute('data-keyline-theme') : '';
    
    //If the next element is a flourish and this is a TOP keyline , then we should target that instead
    if(type === 'top' && targetElement.parentNode.nextElementSibling && targetElement.parentNode.nextElementSibling.nodeName === 'SECTION') {
      for (const child of targetElement.parentNode.nextElementSibling.children) {
        if(child.classList.contains('flourish')) {
          //Push original
          targetElement.classList.add('module--behind');
          child.classList.add('front');
          flipped = child.classList.contains('bottom');

          //INcrement the coujnt
          elements.push(child);

          //Target the flourish
          targetElement = child;
          isFlourish = true;
          break;
        }
      }
    }


    //Alignment classes for bottom (tl, tr, bl, br), we choose one at random, but in certain circumstances they aren't allowed
    if(type === 'bottom') {
      //Remove elements from array
      let aligns = ['tl', 'tr', 'br']; //, 'bl' - removed bl for now as it doesn't look great
      let rotations = {
        'tl' : {start: -14, end: 19},
        'tr' : {start: 14, end: -19},
        'br' : {start: 5, end: -20},
        'bl' : {start: -5, end: 20},
      };
      let themes = this.getModuleThemes(targetElement);

      //Previous element is the same colour OR is a flourish, so no TOP aligns allowed
      if(themes.current === themes.previous || themes.previousIsFlourish) {
        (aligns.indexOf('tl') !== -1) ? aligns.splice(aligns.indexOf('tl'), 1) : null;
        (aligns.indexOf('tr') !== -1) ? aligns.splice(aligns.indexOf('tr'), 1) : null;
      }

      //Next element is the same colour OR is a flourish, so no BOTTOM aligns allowed
      if(themes.current === themes.next || themes.nextIsFlourish) {
        (aligns.indexOf('bl') !== -1) ? aligns.splice(aligns.indexOf('bl'), 1) : null;
        (aligns.indexOf('br') !== -1) ? aligns.splice(aligns.indexOf('br'), 1) : null;
      }

      //Assign a theme
      if(aligns.length > 0) {
        //Pick one at random
        let alignment = aligns[Math.floor(Math.random() * aligns.length)];
        classAlign += 'keyline-generic--align-' + alignment;
        rotation = rotations[alignment]; //{start: 0, end: 0};
      } else {
        //Invalid, so don't write the keyline out
        valid = false;
      }
    }
    
    //Only write if valid
    if(valid) {
      //Create the keyline
      let keyline = document.createElement('div');
      keyline.innerHTML = '<div class="keyline">' + this.keyline + '</div>';
      keyline.classList.add('keyline-generic');
      keyline.classList.add('keyline-generic--' + type);
      if(flipped) { keyline.classList.add('keyline-generic--flipped-v'); }
      if(keylineTheme !== '') { keyline.classList.add('theme-keyline-' + keylineTheme); }
      if(classAlign !== '') { keyline.classList.add(classAlign); }
      
      //Prepend to put behind any content, or append to put in front of flourish
      (!isFlourish && !targetElement.classList.contains('module--header-image')) ? targetElement.querySelector('.content-wrapper').prepend(keyline) : targetElement.append(keyline);

      //Create a timeline and snap to the start
      let timeline = gsap.timeline({paused: true});
      timeline.to(keyline.querySelector('.keyline'), {rotate: rotation.start, duration: 0});
      timeline.to(keyline.querySelector('.keyline'), {rotate: rotation.end, duration: 1, ease:'quad.inOut'});
      gsap.to(keyline.querySelector('.keyline'), {rotate: rotation.start, duration: 0});

      //Register it
      this.keylinesGeneric.push({
        element: targetElement,
        height: this.getGroupHeight(elements), //used by the navigation controller to decide when the alt nav should show
        type: type,
        elements: elements,
        timeline: timeline
      });
    }
  }


}


//--------------------------------------------------------------------------
//  Export
//--------------------------------------------------------------------------
const KeylineController = new _KeylineController();
export default KeylineController;